import { useCallback, useEffect, useState } from 'react';
import useMap from '../hooks/useMap';

export type SvgDefinition = {
  name: string;
  svgString: string;
  height: number;
  width: number;
  /**
   * Stretchable image options for icon-text-fit support.
   * See: https://docs.mapbox.com/mapbox-gl-js/api/map/#map#addimage
   */
  stretchX?: [number, number][];
  stretchY?: [number, number][];
  /**
   * Content area [x1, y1, x2, y2] where text can be placed when using icon-text-fit.
   */
  content?: [number, number, number, number];
};

/**
 * Hook to add svgs to mapbox as images to be used by markers/layers/etc
 * @param svgDefinitions Array of objects defining each svg image
 * name:
 *   The identifier used by mapbox
 * svgString:
 *   An svg definition, _as a string_ - see curatedWaypointSvg.ts for an example.
 *   If we try to use an actual svg file and pass in svgFile.src, for example, next
 *   recognizes that as an asset so the resulting src string is a url to our CDN where the
 *   svg is being hosted (it works locally, but not for an actual deployment).
 *   This would require us to then call map.loadImage which introduces race conditions
 *   and extra waiting time. Instead, if we just keep the svg definition as a string
 *   and convert it to a base-64 encoded string, we can use that directly as the image
 *   src and skip the call to loadImage altogether.
 * height:
 *   The height of the rendered image on the map
 * width:
 *   The width of the rendered image on the map
 * @returns True once the images have been added to mapbox
 */
const useSvgImages = (svgDefinitions: SvgDefinition[], mapId?: string) => {
  const map = useMap(mapId);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set([]));

  const loadImage = useCallback(
    ({ name, svgString, height, width, stretchX, stretchY, content }: SvgDefinition) => {
      if (!map || map.hasImage(name)) {
        setLoadedImages(images => {
          const newSet = new Set(images);
          newSet.add(name);
          return newSet;
        });
        return;
      }

      // Doubling the image size and using a pixelRatio of 2 gives us a much higher-res image
      const img = new Image(width * 2, height * 2);

      img.onload = () => {
        if (!map) {
          return;
        }
        if (map.hasImage(name)) {
          setLoadedImages(images => {
            const newSet = new Set(images);
            newSet.add(name);
            return newSet;
          });
          return;
        }
        // Build addImage options, including stretch regions if provided
        const options: Parameters<typeof map.addImage>[2] = { pixelRatio: 2 };

        // Scale stretch coordinates by 2 to match the doubled image size
        if (stretchX) {
          options.stretchX = stretchX.map(([from, to]) => [from * 2, to * 2] as [number, number]);
        }
        if (stretchY) {
          options.stretchY = stretchY.map(([from, to]) => [from * 2, to * 2] as [number, number]);
        }
        if (content) {
          options.content = [content[0] * 2, content[1] * 2, content[2] * 2, content[3] * 2];
        }

        map.addImage(name, img, options);
        setLoadedImages(images => {
          const newSet = new Set(images);
          newSet.add(name);
          return newSet;
        });
      };

      const src = `data:image/svg+xml;base64,${window.btoa(svgString)}`;
      img.src = src;
    },
    [map]
  );

  useEffect(() => {
    if (!map) {
      return;
    }
    svgDefinitions.forEach(loadImage);

    // When the map's base style is changed, all sources and layers are lost.
    // style.load is triggered when a new base style is loaded, so re-add the svg images whenever this happens.
    // https://github.com/mapbox/mapbox-gl-js/issues/4006
    const onStyleLoad = () => {
      setLoadedImages(new Set([]));
      svgDefinitions.forEach(loadImage);
    };

    map.on('style.load', onStyleLoad);

    return () => {
      map.off('style.load', onStyleLoad);
    };
  }, [loadImage, map, svgDefinitions]);

  return { imagesAreLoaded: svgDefinitions.every(({ name }) => loadedImages.has(name)), loadImage };
};

export function preloadSvg(svgString: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = `data:image/svg+xml;base64,${window.btoa(svgString)}`;
  });
}

export default useSvgImages;
